if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.requiresTourConsent = this.dataset.tourConsent === 'true';
        this.isAgePricingTour = this.dataset.agePricingTour === 'true';
        this.requiresSeminarApplication = this.dataset.seminarApplication === 'true';
        this.addToCartLabel = this.dataset.addToCartLabel || '';
        this.tourConsentCheckbox = this.requiresTourConsent ? this.querySelector('[data-tour-consent-input]') : null;
        this.variantUnavailable = this.submitButton.dataset.variantUnavailable === 'true';
        this.presaleLocked = this.dataset.presaleLocked === 'true';
        this.submitButton.dataset.consentLocked = 'false';
        this.submitButton.dataset.seminarLocked = this.requiresSeminarApplication ? 'true' : 'false';
        this.submitButton.dataset.presaleLocked = this.presaleLocked ? 'true' : 'false';
        if (this.requiresTourConsent) {
          this.enforceTourConsent();
          // フォーム内の全変更（同意チェック・未成年承諾・生年月日セレクト等）を一括監視
          this.form.addEventListener('change', this.enforceTourConsent.bind(this));
        } else {
          this.syncSubmitState();
        }
        if (this.requiresSeminarApplication) {
          this.syncSeminarApplicationLock();
          this.form.addEventListener('change', this.syncSeminarApplicationLock.bind(this));
        }
        this.syncPresaleVisibility();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.requiresTourConsent) {
          const { validationState, state, consentLocked } = this.syncTourConsentLock('submit');
          if (consentLocked) {
            const blockingMessage = this.getTourBlockingMessage(validationState, state);
            if (blockingMessage) alert(blockingMessage);
            return;
          }
        }
        if (this.requiresSeminarApplication && this.syncSeminarApplicationLock()) {
          this.handleErrorMessage('申込区分で「会員」または「一般」を選択してください。');
          const firstCustomerKindInput = this.form.querySelector('[data-seminar-customer-kind]');
          if (firstCustomerKindInput && typeof firstCustomerKindInput.focus === 'function') {
            firstCustomerKindInput.focus();
          }
          return;
        }
        if (this.submitButton.hasAttribute('disabled')) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const triggerCartAttributesSync = () => {
          const syncModule = window.MFCartAttributesSync;
          if (!syncModule || typeof syncModule.syncApplicationDataAttribute !== 'function') {
            return Promise.resolve();
          }
          return syncModule
            .syncApplicationDataAttribute({
              fetchImpl: fetch,
              now: () => new Date(),
              consoleObj: console,
            })
            .catch(() => {});
        };

        const finishApplicationChange = window.MFCartApplication?.beginChange();
        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              await triggerCartAttributesSync();
              window.location = window.routes.cart_url;
              return;
            }

            await triggerCartAttributesSync();

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            finishApplicationChange?.();
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.variantUnavailable = disable;
        if (disable) {
          this.submitButton.dataset.variantUnavailable = 'true';
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.dataset.variantUnavailable = 'false';
          this.submitButtonText.textContent = this.addToCartLabel || window.variantStrings.addToCart;
        }
        this.syncSubmitState();
      }

      setPresaleLocked(locked) {
        const wasLocked = this.presaleLocked;
        this.presaleLocked = !!locked;
        this.dataset.presaleLocked = this.presaleLocked ? 'true' : 'false';
        this.submitButton.dataset.presaleLocked = this.presaleLocked ? 'true' : 'false';
        this.syncSubmitState();
        if (wasLocked && !this.presaleLocked) {
          this.requestDynamicTourVisibilitySync();
        }
      }

      getPresaleManagedElements() {
        const managedElements = [];
        const formId = this.form && this.form.id ? String(this.form.id) : '';
        const sectionId = this.dataset && this.dataset.sectionId ? String(this.dataset.sectionId) : '';
        let quantityWrap = null;
        if (sectionId && typeof document !== 'undefined' && typeof document.getElementById === 'function') {
          quantityWrap = document.getElementById(`Quantity-Form-${sectionId}`);
        }
        if (!quantityWrap && formId && typeof document !== 'undefined' && document.querySelector) {
          const quantityInput = document.querySelector(
            `.quantity__input[form="${formId}"], input[name="quantity"][form="${formId}"]`
          );
          quantityWrap =
            quantityInput && typeof quantityInput.closest === 'function'
              ? quantityInput.closest('.product-form__input') || quantityInput.closest('quantity-input')
              : null;
        }
        if (quantityWrap) managedElements.push({ element: quantityWrap, restoreMode: 'snapshot' });

        const consentWrap = this.querySelector('[data-tour-consent-wrapper]');
        if (consentWrap) managedElements.push({ element: consentWrap, restoreMode: 'snapshot' });

        const seminarApplicationFields = this.querySelector('[data-seminar-application-fields]');
        if (seminarApplicationFields) {
          managedElements.push({ element: seminarApplicationFields, restoreMode: 'snapshot' });
        }

        const { birthdateHidden, minorWrap } = this.getTourContractElements();
        if (minorWrap) managedElements.push({ element: minorWrap, restoreMode: 'dynamic' });
        const birthdateSection =
          birthdateHidden && typeof birthdateHidden.closest === 'function'
            ? birthdateHidden.closest('.tour-applicant-birthdate')
            : null;
        if (birthdateSection) managedElements.push({ element: birthdateSection, restoreMode: 'snapshot' });

        const attendeesFieldset = this.form ? this.form.querySelector('.tour-attendees') : null;
        if (attendeesFieldset) managedElements.push({ element: attendeesFieldset, restoreMode: 'dynamic' });

        return managedElements;
      }

      requestDynamicTourVisibilitySync() {
        if (!this.form || typeof this.form.dispatchEvent !== 'function') return;
        const event =
          typeof CustomEvent === 'function'
            ? new CustomEvent('tour:visibility-sync-request', { bubbles: true })
            : { type: 'tour:visibility-sync-request' };
        this.form.dispatchEvent(event);
      }

      syncPresaleVisibility() {
        const managedElements = this.getPresaleManagedElements();
        managedElements.forEach((entry) => {
          const element = entry && entry.element ? entry.element : entry;
          const restoreMode = entry && entry.restoreMode ? entry.restoreMode : 'snapshot';
          if (!element || !element.dataset) return;
          if (this.presaleLocked) {
            if (element.dataset.presaleVisibilityStored !== 'true') {
              element.dataset.presaleHiddenBeforeLock = element.hidden ? 'true' : 'false';
              element.dataset.presaleVisibilityStored = 'true';
              element.dataset.presaleRestoreMode = restoreMode;
            }
            element.hidden = true;
            return;
          }

          if (element.dataset.presaleVisibilityStored === 'true') {
            if (element.dataset.presaleRestoreMode === 'snapshot') {
              element.hidden = element.dataset.presaleHiddenBeforeLock === 'true';
            }
            delete element.dataset.presaleHiddenBeforeLock;
            delete element.dataset.presaleVisibilityStored;
            delete element.dataset.presaleRestoreMode;
          }
        });
      }

      syncSubmitState() {
        const consentLocked = this.submitButton.dataset.consentLocked === 'true';
        const seminarLocked = this.submitButton.dataset.seminarLocked === 'true';
        const shouldDisable = this.variantUnavailable || this.presaleLocked || consentLocked || seminarLocked;

        if (shouldDisable) {
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          this.submitButton.removeAttribute('disabled');
        }

        this.submitButton.classList.toggle('presale-disabled', this.presaleLocked);
        this.submitButton.classList.toggle(
          'tour-consent-disabled',
          consentLocked && !this.variantUnavailable && !this.presaleLocked
        );
        this.submitButton.classList.toggle(
          'seminar-application-disabled',
          seminarLocked && !this.variantUnavailable && !this.presaleLocked && !consentLocked
        );
        this.syncPresaleVisibility();
      }

      isSeminarCustomerKindSelected() {
        if (!this.requiresSeminarApplication) return true;
        return !!this.form.querySelector('[data-seminar-customer-kind]:checked');
      }

      syncSeminarApplicationLock() {
        const seminarLocked = !this.isSeminarCustomerKindSelected();
        this.submitButton.dataset.seminarLocked = seminarLocked ? 'true' : 'false';
        this.syncSubmitState();
        return seminarLocked;
      }

      requestTourValidation(mode = 'silent') {
        if (!this.requiresTourConsent) {
          return this.getDefaultTourValidationState();
        }

        const detail = {
          mode,
          state: null,
        };
        this.form.dispatchEvent(
          new CustomEvent('tour:validation-request', {
            bubbles: true,
            detail,
          })
        );

        return this.normalizeTourValidationState(
          detail.state || this.getFallbackTourValidationOverrides()
        );
      }

      getFallbackTourValidationOverrides() {
        return {
          birthdateOk: this.isBirthdateComplete(),
          agePricingOk: true,
          attendeesOk: this.areAttendeeFieldsComplete(),
          ...this.getAgePricingContractState(),
        };
      }

      getDefaultTourValidationState() {
        return {
          birthdateOk: true,
          agePricingOk: true,
          attendeesOk: true,
          minorConsentRequired: false,
          inlineErrorShown: false,
          message: '',
          agePricingState: this.isAgePricingTour ? 'pending' : 'disabled',
          expectedVariantId: '',
        };
      }

      mergeTourValidationState(validationState = {}) {
        return {
          ...this.getDefaultTourValidationState(),
          ...validationState,
        };
      }

      normalizeTourValidationState(validationState = {}) {
        const mergedValidationState = this.mergeTourValidationState(validationState);
        return {
          ...mergedValidationState,
          agePricingOk:
            this.isAgePricingSelectionValid(mergedValidationState) && mergedValidationState.agePricingOk,
        };
      }

      isBirthdateComplete() {
        const { birthdateHidden } = this.getTourContractElements();
        const bdHidden = birthdateHidden;
        return bdHidden ? !!bdHidden.value : !this.requiresTourConsent;
      }

      isAgePricingSelectionValid(validationState = null) {
        if (!this.isAgePricingTour) return true;
        const contractState = this.getAgePricingContractState();
        const agePricingState = validationState && typeof validationState.agePricingState === 'string'
          ? validationState.agePricingState
          : contractState.state;
        const expectedVariantId = validationState && typeof validationState.expectedVariantId === 'string'
          ? validationState.expectedVariantId
          : contractState.expectedVariantId;
        const currentVariantId = this.variantIdInput ? this.variantIdInput.value : '';
        return agePricingState === 'ok' && !!expectedVariantId && String(currentVariantId) === String(expectedVariantId);
      }

      isMinorConsentSatisfied(validationState = null) {
        const { minorWrap, minorInput } = this.getTourContractElements();
        const minorConsentRequired =
          validationState && typeof validationState.minorConsentRequired === 'boolean'
            ? validationState.minorConsentRequired
            : !!(minorWrap && !minorWrap.hidden);
        return minorConsentRequired ? !!(minorInput && minorInput.checked) : true;
      }

      areAttendeeFieldsComplete() {
        const attendeesFieldset = this.form.querySelector('.tour-attendees');
        if (!attendeesFieldset || attendeesFieldset.hidden) return true;
        const requiredInputs = attendeesFieldset.querySelectorAll('input[required], select[required]');
        for (const input of requiredInputs) {
          if (!input.value.trim()) return false;
        }
        return true;
      }

      getTourFormState(validationState = this.requestTourValidation('silent')) {
        const normalizedValidationState = this.normalizeTourValidationState(validationState);
        return this.createTourConsentState(normalizedValidationState);
      }

      createTourConsentState(normalizedValidationState) {
        return {
          consentGiven: this.isTourConsentGiven(),
          birthdateOk: normalizedValidationState.birthdateOk,
          agePricingOk: normalizedValidationState.agePricingOk,
          minorOk: this.isMinorConsentSatisfied(normalizedValidationState),
          attendeesOk: normalizedValidationState.attendeesOk,
        };
      }

      isTourConsentGiven() {
        return !!(this.tourConsentCheckbox && this.tourConsentCheckbox.checked);
      }

      getValidationStateBlockingMessage(validationState) {
        if (validationState && validationState.message && !validationState.inlineErrorShown) {
          return validationState.message;
        }
        return '';
      }

      getTourRuleBlockingMessages(validationState, state) {
        const blockingRules = [
          {
            blocked: !state.consentGiven,
            message: '「ツアー参加規約を読み、同意しました」にチェックしてください。',
          },
          {
            blocked: !state.minorOk,
            message: '「未成年が参加することを承諾します」にチェックしてください。',
          },
        ];

        const messages = blockingRules.filter((rule) => rule.blocked).map((rule) => rule.message);
        const hasInlineValidationError = !!(validationState && validationState.inlineErrorShown);
        if (!state.birthdateOk && !hasInlineValidationError && !this.getValidationStateBlockingMessage(validationState)) {
          messages.push('申込者（代表者）の生年月日を入力してください。');
        }
        return messages;
      }

      getTourBlockingReasons(validationState, state) {
        const reasons = [];
        const validationMessage = this.getValidationStateBlockingMessage(validationState);
        if (validationMessage) reasons.push(validationMessage);
        return reasons.concat(this.getTourRuleBlockingMessages(validationState, state));
      }

      shouldLockTourConsent(validationState, state) {
        const hasBlockingReasons = this.getTourBlockingReasons(validationState, state).length > 0;
        const hasValidationFailure = !state.birthdateOk || !state.agePricingOk || !state.attendeesOk;
        return hasBlockingReasons || hasValidationFailure;
      }

      getTourBlockingMessage(validationState, state) {
        return this.getTourBlockingReasons(validationState, state)[0] || '';
      }

      getTourContractElements() {
        return {
          birthdateHidden: this.form.querySelector('[data-birthdate-hidden]'),
          agePricingStateInput: this.form.querySelector('[data-age-pricing-state]'),
          expectedVariantInput: this.form.querySelector('[data-age-pricing-expected-variant]'),
          minorWrap: this.form.querySelector('[data-tour-minor-consent-wrapper]'),
          minorInput: this.form.querySelector('[data-tour-minor-consent-input]'),
        };
      }

      getDefaultAgePricingContractState() {
        return {
          state: this.isAgePricingTour ? 'pending' : 'disabled',
          expectedVariantId: '',
        };
      }

      mergeAgePricingContractState(overrides = {}) {
        return {
          ...this.getDefaultAgePricingContractState(),
          ...overrides,
        };
      }

      getAgePricingContractState() {
        const { agePricingStateInput, expectedVariantInput } = this.getTourContractElements();
        const overrides = {};
        if (agePricingStateInput) overrides.state = String(agePricingStateInput.value || '');
        if (expectedVariantInput) overrides.expectedVariantId = String(expectedVariantInput.value || '');
        return this.mergeAgePricingContractState(overrides);
      }

      syncTourConsentLock(mode = 'silent') {
        const validationState = this.requestTourValidation(mode);
        const state = this.getTourFormState(validationState);
        const consentLocked = this.shouldLockTourConsent(validationState, state);
        this.submitButton.dataset.consentLocked = consentLocked ? 'true' : 'false';
        this.syncSubmitState();
        return { validationState, state, consentLocked };
      }

      enforceTourConsent() {
        if (!this.requiresTourConsent || !this.tourConsentCheckbox) return;
        this.syncTourConsentLock('silent');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
